import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Zap, 
  Mail, 
  Phone, 
  CreditCard, 
  BrainCircuit,
  CheckCircle, 
  XCircle, 
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Info,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IntegrationStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [setupGuide, setSetupGuide] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/integrations/status`);
      setStatus(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch integration status');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSetupGuide = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/integrations/setup-guide`);
      setSetupGuide(response.data);
    } catch (err) {
      console.error('Failed to fetch setup guide:', err);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchSetupGuide();
  }, []);

  const getIntegrationIcon = (key) => {
    const icons = {
      twilio_sms: Phone,
      sendgrid_email: Mail,
      plaid_banking: CreditCard,
      stripe_payments: CreditCard,
      emergent_llm: BrainCircuit
    };
    return icons[key] || Zap;
  };

  const getStatusBadge = (integration) => {
    if (integration.enabled && integration.mode === 'real') {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle className="h-3 w-3 mr-1" />
          Active
        </Badge>
      );
    } else if (integration.mode === 'mocked') {
      return (
        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
          <AlertCircle className="h-3 w-3 mr-1" />
          Mocked
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-gray-100 text-gray-800 border-gray-200">
          <XCircle className="h-3 w-3 mr-1" />
          Disabled
        </Badge>
      );
    }
  };

  if (loading && !status) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-center gap-2 text-gray-500">
          <RefreshCw className="h-5 w-5 animate-spin" />
          Loading integration status...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-center gap-2 text-red-500">
          <XCircle className="h-5 w-5" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Zap className="h-5 w-5 text-blue-600" />
              Third-Party Integrations
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Status of external service connections
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchStatus}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Stats */}
        {status?.summary && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900">{status.summary.total}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{status.summary.enabled_real}</div>
              <div className="text-xs text-green-600">Active</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-yellow-600">{status.summary.mocked}</div>
              <div className="text-xs text-yellow-600">Mocked</div>
            </div>
          </div>
        )}

        {/* Integration List */}
        <div className="space-y-3">
          {status?.integrations && Object.entries(status.integrations).map(([key, integration]) => {
            const Icon = getIntegrationIcon(key);
            return (
              <div 
                key={key}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  integration.enabled && integration.mode === 'real' 
                    ? 'bg-green-50 border-green-200' 
                    : integration.mode === 'mocked'
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    integration.enabled && integration.mode === 'real'
                      ? 'bg-green-100'
                      : integration.mode === 'mocked'
                      ? 'bg-yellow-100'
                      : 'bg-gray-100'
                  }`}>
                    <Icon className={`h-5 w-5 ${
                      integration.enabled && integration.mode === 'real'
                        ? 'text-green-600'
                        : integration.mode === 'mocked'
                        ? 'text-yellow-600'
                        : 'text-gray-600'
                    }`} />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 text-sm">
                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </div>
                    <div className="text-xs text-gray-500">{integration.description}</div>
                  </div>
                </div>
                {getStatusBadge(integration)}
              </div>
            );
          })}
        </div>
      </div>

      {/* Setup Guide Toggle */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <button
          onClick={() => setShowGuide(!showGuide)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-gray-900">Setup Guide</span>
            <span className="text-sm text-gray-500">- How to enable real integrations</span>
          </div>
          {showGuide ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {showGuide && setupGuide && (
          <div className="border-t p-4 space-y-4">
            <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
              <strong>Note:</strong> {setupGuide.note}
            </p>
            
            {Object.entries(setupGuide.instructions).map(([key, guide]) => (
              <div key={key} className="border rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">{guide.name}</h4>
                <div className="text-xs text-gray-500 mb-3">Cost: {guide.cost}</div>
                <ol className="text-sm text-gray-600 space-y-1">
                  {guide.steps.map((step, idx) => (
                    <li key={idx} className="font-mono text-xs bg-gray-50 p-1 rounded">
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <ExternalLink className="h-4 w-4 text-gray-500" />
          Provider Dashboards
        </h4>
        <div className="flex flex-wrap gap-2">
          <a 
            href="https://console.twilio.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 flex items-center gap-1 transition-colors"
          >
            Twilio Console
            <ExternalLink className="h-3 w-3" />
          </a>
          <a 
            href="https://app.sendgrid.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 flex items-center gap-1 transition-colors"
          >
            SendGrid Dashboard
            <ExternalLink className="h-3 w-3" />
          </a>
          <a 
            href="https://dashboard.plaid.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 flex items-center gap-1 transition-colors"
          >
            Plaid Dashboard
            <ExternalLink className="h-3 w-3" />
          </a>
          <a 
            href="https://dashboard.stripe.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 flex items-center gap-1 transition-colors"
          >
            Stripe Dashboard
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default IntegrationStatus;
